import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/api';
import PremiumUpgradeButton from '../components/PremiumUpgradeButton';

interface Booking {
  id: number;
  service: string;
  date: string;
  time: string;
  message: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: string;
  user: {
    name: string;
    email: string;
    phone?: string;
  };
  provider: {
    businessName: string;
    contactPerson: string;
    phone: string;
  };
}

interface Provider {
  id: number;
  businessName: string;
  isPremium: boolean;
  isFeatured: boolean;
  premiumExpiresAt?: string;
}

const ProviderDashboard: React.FC = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchBookings = useCallback(async () => {
    try {
      console.log('Fetching bookings for user ID:', user?.id);
      
      if (!user?.id) {
        setError('No user ID available. Please log in again.');
        return;
      }
      
      // First, check if user is a provider by looking up their service provider record
      const providerResponse = await apiRequest(`/providers/user/${user.id}`);
      
      if (providerResponse.status === 404) {
        setError('You are not registered as a service provider. Please register as a provider first.');
        return;
      }
      
      if (!providerResponse.ok) {
        throw new Error('Failed to check provider status');
      }
      
      const providerData = await providerResponse.json();
      console.log('Found provider:', providerData);
      setProvider(providerData);
      
      // Now fetch bookings for this provider
      const response = await apiRequest(`/bookings/provider/${providerData.id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }
      
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleStatusUpdate = async (bookingId: number, status: 'accepted' | 'declined') => {
    try {
      setLoading(true);
      const response = await apiRequest(`/bookings/${bookingId}/${status}`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Failed to ${status} booking`);
      }
      
      // Update local state with the updated booking from response
      setBookings(bookings.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status } 
          : booking
      ));
      
      // Show success message
      setSnackbar({
        open: true,
        message: data.message || `Booking ${status} successfully`,
        severity: 'success'
      });
      
      // Refresh bookings to get the latest data
      setTimeout(() => {
        fetchBookings();
      }, 1000);
      
    } catch (err: any) {
      console.error(`Error ${status}ing booking:`, err);
      setSnackbar({
        open: true,
        message: err.message || `Failed to ${status} booking`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            My Bookings
          </Typography>
          {provider && (
            <Box>
              <PremiumUpgradeButton
                providerId={provider.id}
                currentTier={provider.isFeatured ? 'platinum' : provider.isPremium ? 'premium' : 'basic'}
              />
            </Box>
          )}
        </Box>

        {provider && (
          <Card sx={{ mb: 3, bgcolor: provider.isPremium || provider.isFeatured ? 'primary.light' : 'grey.50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{provider.businessName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status: {provider.isFeatured ? 'Platinum' : provider.isPremium ? 'Premium' : 'Basic'} Listing
                  </Typography>
                  {provider.premiumExpiresAt && (
                    <Typography variant="body2" color="text.secondary">
                      Expires: {new Date(provider.premiumExpiresAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {bookings.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" align="center">
                No bookings found
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {bookings.map((booking) => (
              <Grid item xs={12} md={6} key={booking.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6">
                        {booking.service}
                      </Typography>
                      <Chip 
                        label={booking.status} 
                        color={getStatusColor(booking.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Client:</strong> {booking.user.name}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Date:</strong> {formatDate(booking.date)} at {booking.time}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Email:</strong> {booking.user.email}
                    </Typography>
                    
                    {booking.user.phone && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Phone:</strong> {booking.user.phone}
                      </Typography>
                    )}
                    
                    {booking.message && (
                      <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <strong>Message:</strong> {booking.message}
                      </Typography>
                    )}
                    
                    {booking.status === 'pending' && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleStatusUpdate(booking.id, 'accepted')}
                          disabled={loading}
                        >
                          {loading ? 'Processing...' : 'Accept'}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleStatusUpdate(booking.id, 'declined')}
                          disabled={loading}
                        >
                          {loading ? 'Processing...' : 'Decline'}
                        </Button>
                      </Box>
                    )}
                    
                    {booking.status !== 'pending' && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Booking {booking.status} on {new Date(booking.createdAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProviderDashboard; 